import { createJSONStorage } from 'jotai/utils';
import { type z } from 'zod';

interface SyncStorage<Value> {
  getItem: (key: string, initialValue: Value) => Value;
  setItem: (key: string, newValue: Value) => void;
  removeItem: (key: string) => void;
  subscribe?: (key: string, callback: (value: Value) => void, initialValue: Value) => (() => void) | undefined;
}

export interface CreateSafeStorageOptions {
  /** Called with the Zod error + key when a stored value fails validation (before reset to fallback). */
  onValidationError?: (error: z.ZodError, key: string) => void;
  /** Called when a write is rejected because storage is full (data was NOT saved). */
  onQuotaExceeded?: (error: DOMException, key: string) => void;
  /** Cross-tab `storage`-event sync (schema-revalidated). Default on; pass `false` to opt out. */
  crossTabSync?: boolean;
}

// WHY: browsers disagree on how they surface a full-storage write — modern engines throw a DOMException
// named QuotaExceededError / NS_ERROR_DOM_QUOTA_REACHED, older ones only set the legacy numeric code 22.
const isQuotaExceededError = (error: unknown): error is DOMException => {
  if (!(error instanceof Error)) return false;
  const hasLegacyCode =
    'code' in error &&
    typeof (error as { code?: unknown }).code === 'number' &&
    (error as { code: number }).code === 22;
  return error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED' || hasLegacyCode;
};

// INVARIANT: This utility MUST be used for all persistent Jotai atoms
// to ensure runtime data integrity and prevent QuotaExceeded crashes.
export function createSafeStorage<T>(
  schema: z.ZodType<T>,
  fallbackValue: T,
  storageType: 'localStorage' | 'sessionStorage' = 'localStorage',
  options?: CreateSafeStorageOptions
) {
  const baseStorage = createJSONStorage<T>(() => {
    const impl = typeof window !== 'undefined' ? window[storageType] : undefined;
    return (
      impl || {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {}
      }
    );
  });

  const safeStorage: SyncStorage<T> = {
    getItem: (key: string, initialValue: T): T => {
      try {
        const item = baseStorage.getItem(key, initialValue);
        if (item === null) return fallbackValue;

        // Validate with Zod
        const result = schema.safeParse(item);

        if (!result.success) {
          console.warn(`[SafeStorage] Data validation failed for key "${key}". Resetting to default.`, result.error);

          // Back up the corrupt value under a recovery key before resetting — forensics instead of
          // silent data loss. Best-effort: a failed backup must never block the reset-to-fallback.
          try {
            const backupKey = `${key}_corrupt_backup`;
            window.localStorage.setItem(backupKey, JSON.stringify(item));
            console.warn(`[SafeStorage] Corrupt data backed up to "${backupKey}".`);
          } catch {
            // ignore — backup is best-effort
          }

          options?.onValidationError?.(result.error, key);
          return fallbackValue;
        }

        return result.data;
      } catch (error) {
        console.error(`[SafeStorage] Failed to load key "${key}".`, error);
        return fallbackValue;
      }
    },

    setItem: (key: string, newValue: T) => {
      try {
        baseStorage.setItem(key, newValue);
      } catch (error) {
        if (isQuotaExceededError(error)) {
          console.error(`[SafeStorage] Quota exceeded while saving key "${key}". Data was not saved.`);
          options?.onQuotaExceeded?.(error, key);
        } else {
          console.error(`[SafeStorage] Failed to save key "${key}".`, error);
        }
      }
    },

    removeItem: (key: string) => {
      baseStorage.removeItem(key);
    },

    subscribe: (key: string, callback: (value: T) => void, initialValue: T) => {
      if (!baseStorage.subscribe) return undefined;

      return baseStorage.subscribe(
        key,
        (value: T) => {
          const result = schema.safeParse(value);
          if (result.success) {
            callback(result.data);
          } else {
            // A malformed cross-tab write should never propagate downstream — drop it, keep current state.
            console.warn(`[SafeStorage] Received invalid data from storage event for "${key}". Ignoring.`);
          }
        },
        initialValue
      );
    }
  };

  // Opt out of cross-tab sync entirely when requested (e.g. session-scoped, single-tab state).
  if (options?.crossTabSync === false) {
    delete safeStorage.subscribe;
  }

  return safeStorage;
}
